'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';
import { CTAButton } from '@/shadcn/ui/button';
import dotsGradientBg from '@/public/global/dots-gradient-bg.png';
import Image from 'next/image';

const ease = [0.25, 0.1, 0.25, 1] as const;

function FounderImageBlock({ data }: { data: PageAboutUsSchema }) {
  return (
    <div className="flex flex-col gap-[2.25rem]">
      {data.founderImage && (
        <div
          className="relative w-full h-[50rem] lg:h-auto overflow-hidden"
          style={{ aspectRatio: '510/618' }}
        >
          <GroqImage
            src={data.founderImage}
            alt={data.founderName ?? 'Founder'}
            className="!w-full !h-full !object-cover"
          />
        </div>
      )}
      {(data.founderName || data.founderRole) && (
        <div className="flex flex-col gap-[0.75rem]">
          {data.founderName && (
            <p className="mb-0 font-geist text-[3.5rem] font-medium leading-[1.12] tracking-[-0.14rem] text-black">
              {data.founderName}
            </p>
          )}
          {data.founderRole && (
            <p className="mb-0 font-geist-mono text-[1.75rem] font-medium uppercase tracking-[-0.053rem] text-[#228e71]">
              {data.founderRole}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function FounderStorySection({
  data,
}: {
  data: PageAboutUsSchema;
}) {
  const timeline = data.founderTimeline ?? [];

  if (!data.founderChip && !data.founderTitle && !data.founderImage)
    return null;

  return (
    <section className="relative  w-full bg-white pt-[5rem] lg:pb-[15rem] pb-[7.37rem] lg:pt-[15rem]">
      <div className="absolute w-[calc(100%+50rem)] lg:w-full h-[48rem] top-0 left-1/2 -translate-x-1/2 mix-blend-exclusion pointer-events-none select-none">
        <Image fill alt="Background pattern" src={dotsGradientBg} />
      </div>
      <div className="mx-auto  lg:px-0 px-[3rem] w-fit flex  flex-col gap-[3rem] lg:flex-row lg:items-start lg:gap-[13rem]">
        {/* Desktop only: left column (image + name) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease }}
          className="hidden lg:flex flex-col gap-[3rem] lg:w-[63.75rem] shrink-0 lg:sticky lg:top-[6rem]"
        >
          <FounderImageBlock data={data} />
        </motion.div>

        {/* Right column (desktop) / Main column (mobile) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease, delay: 0.15 }}
          className="flex  flex-col gap-[3rem] lg:gap-[4rem] lg:w-[63rem]"
        >
          {/* Text block: chip + title + body — centered on mobile */}
          <div className="flex flex-col gap-[2.5rem] items-center text-center lg:items-start lg:text-left">
            <div className="flex flex-col gap-[1.25rem] items-center lg:items-start">
              {data.founderChip && (
                <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                  {data.founderChip}
                </p>
              )}
              {data.founderTitle && (
                <div className="richtext lg:richtext-start prose-em:italic prose-em:text-[#003828]">
                  <PortableText
                    value={data.founderTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
            {data.founderBody && (
              <div className="richtext lg:richtext-start prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1rem] prose-p:text-black/80">
                <PortableText
                  value={data.founderBody}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {/* Mobile only: image + name (between text and timeline) */}
          <div className="lg:hidden flex flex-col gap-[4rem]">
            <FounderImageBlock data={data} />

            {/* Timeline */}
            {!!timeline.length && (
              <div className="flex flex-col">
                {timeline.map((item, index) => (
                  <div key={item._key ?? index} className="flex flex-col">
                    <div className="h-px w-full bg-black/10" />
                    <div className="flex items-start justify-between py-[2.875rem]">
                      <span className="shrink-0 font-geist-mono text-[1.75rem] font-medium leading-none tracking-[-0.053rem] text-[#228e71] pt-[0.5rem]">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      {item.content && (
                        <div className="richtext richtext-start flex-1 ml-[2.125rem] flex flex-col gap-[1.25rem] prose-headings:text-[3rem] prose-headings:font-medium prose-headings:leading-[1.12] prose-headings:tracking-[-0.12rem] prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1rem] prose-p:text-black/60">
                          <PortableText
                            value={item.content}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="h-px w-full bg-black/10" />
              </div>
            )}

            {data.founderButton && (
              <div className="w-full lg:w-fit">
                <CTAButton data={data.founderButton} />
              </div>
            )}
          </div>

          {/* Desktop only: timeline */}
          {!!timeline.length && (
            <div className="hidden lg:flex flex-col">
              {timeline.map((item, index) => (
                <div key={item._key ?? index} className="flex flex-col">
                  <div className="h-px w-full bg-black/10" />
                  <div className="flex gap-[4.88rem] py-[4rem]">
                    <span className="shrink-0 font-geist-mono text-[1.75rem] font-medium leading-none tracking-[-0.053rem] text-[#228e71]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {item.content && (
                      <div className="richtext richtext-start flex-1 flex flex-col gap-[1.25rem] prose-headings:text-[3rem] prose-headings:font-medium prose-headings:leading-[1.12] prose-headings:tracking-[-0.12rem] prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1rem] prose-p:text-black/60">
                        <PortableText
                          value={item.content}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="h-px w-full bg-black/10" />
            </div>
          )}

          {data.founderButton && (
            <div className="hidden lg:block w-full lg:w-fit">
              <CTAButton data={data.founderButton} />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
